import {
  myTasksQueryKey,
  type SchedulingTaskSummary,
  type Task,
  taskByIdQueryKey,
  tasksQueryKey,
  useCompleteTaskMutation,
  useTaskSchedulingContextQuery,
} from '@cartwheel/api/v1'
import { Button, LoadingView, useToast } from '@cartwheel/components'
import { formatAppointmentDateTime } from '@cartwheel/string-utils'
import { useQueryClient } from '@tanstack/react-query'
import { trackTaskResourceLink } from './task-analytics'

function JourneyCard({ journey }: { journey: SchedulingTaskSummary['journeys'][number] }) {
  const appt = journey.last_scheduled_appointment
  return (
    <div className="space-y-1 rounded-lg border border-sand bg-white p-4 shadow-sm">
      <p className="text-base font-semibold leading-[1.5] text-charcoal">{journey.service_name}</p>
      {journey.clinician_name && (
        <p className="text-sm leading-[1.5] text-charcoal-50">
          Clinician: <span className="font-medium text-charcoal">{journey.clinician_name}</span>
        </p>
      )}
      {appt ? (
        <p className="text-sm leading-[1.5] text-charcoal-50">
          Last appointment:{' '}
          <span className="font-medium text-charcoal">
            {formatAppointmentDateTime(appt.date, appt.start_time)}
            {appt.clinician_name && appt.clinician_name !== journey.clinician_name
              ? ` with ${appt.clinician_name}`
              : ''}
          </span>
        </p>
      ) : (
        <p className="text-sm leading-[1.5] text-charcoal-50">No appointments scheduled yet</p>
      )}
    </div>
  )
}

function TaskDescription({ task }: { task: Task }) {
  return (
    <section>
      <h3 className="text-base font-semibold text-charcoal">Description</h3>
      <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">{task.description}</p>
    </section>
  )
}

function ActionRequiredSteps({
  task,
  healthieUrl,
  appointmentsNeeded,
}: {
  task: Task
  healthieUrl?: string | null
  appointmentsNeeded: number
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Action required</h3>
      <ol className="list-decimal list-inside space-y-1 text-sm leading-[1.5] text-charcoal-50">
        <li>
          {healthieUrl ? (
            <a
              href={healthieUrl}
              target="_blank"
              rel="noreferrer"
              className="text-forest underline"
              onClick={() => trackTaskResourceLink(task, 'healthie')}
            >
              Open client in Healthie
            </a>
          ) : (
            'Open client in Healthie'
          )}
        </li>
        <li>
          Book {appointmentsNeeded} session{appointmentsNeeded !== 1 ? 's' : ''} — ideally same
          clinician and time slot
        </li>
        <li>Return here and click &ldquo;Mark complete&rdquo;</li>
      </ol>
    </section>
  )
}

function CanceledNotice() {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Task Canceled</h3>
      <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
        <p className="text-sm leading-[1.5] text-charcoal-50">
          This task was canceled and did not require completion.
        </p>
      </div>
    </section>
  )
}

export function ScheduleAdditionalSessionsWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const contextQuery = useTaskSchedulingContextQuery({ path: { task_id: task.id } })

  const { mutate: complete, isPending } = useCompleteTaskMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: taskByIdQueryKey({ path: { id: task.id } }) })
      toast.success({ description: 'Task marked complete' })
      onClose()
    },
    onError: () => {
      toast.error({ description: 'Failed to complete task' })
    },
  })

  if (task.status === 'canceled') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <CanceledNotice />
      </div>
    )
  }

  if (task.status === 'completed') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <section className="space-y-3">
          <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Completed</h3>
          <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
            <p className="text-sm leading-[1.5] text-charcoal-50">
              Sessions have been scheduled for this client.
            </p>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />

      {contextQuery.isPending && <LoadingView />}

      {contextQuery.isError && (
        <p className="text-sm text-charcoal-50">
          Failed to load scheduling context. Please try again.
        </p>
      )}

      {contextQuery.data && (
        <>
          <section className="space-y-3">
            <h3 className="text-base font-semibold leading-[1.5] text-charcoal">
              Sessions to schedule:{' '}
              <span className="text-forest">{contextQuery.data.appointments_needed}</span>
            </h3>
            {contextQuery.data.journeys.map((journey) => (
              <JourneyCard key={journey.id} journey={journey} />
            ))}
          </section>

          {canWork && (
            <>
              <ActionRequiredSteps
                task={task}
                healthieUrl={contextQuery.data.healthie_url}
                appointmentsNeeded={contextQuery.data.appointments_needed}
              />

              <Button onClick={() => complete({ path: { id: task.id } })} disabled={isPending}>
                {isPending ? 'Saving…' : 'Mark complete'}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  )
}
